import {
  Component,
  forwardRef,
  inject,
  Injector,
  OnDestroy,
  Optional,
  signal, ViewChild
} from '@angular/core';
import {MatIcon} from '@angular/material/icon';
import {ObjectPageLayoutComponent} from './object-page-layout/object-page-layout.component';
import {ActivatedRoute, NavigationEnd, Router, RouterModule, RouterOutlet} from '@angular/router';
import {
  NavMenuItem,
  ObjectPageNavigationComponent,
} from './ui/object-page-navigation/object-page-navigation.component';
import {CurrentObjectContext} from './current-object.context';
import {iconToMaterialSymbol, objectTypeToMatSymbol} from '../shared/material-icons';
import {OperationDef} from '../core/definitions/operation-def';
import {ActionMenuItem} from './ui/object-page-header/menu-group-button/menu-group-button.component';
import {MediaHelperService} from '../core/media-helper.service';
import {SharedModule} from '../shared/shared.module';
import {ContentMenu} from '../core/definitions/object-content-tab/content-menus';
import {TranslateService} from '@ngx-translate/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {OperationDialogService} from '../operations/operation-dialog.service';
import {OperationAndSectionsContainerParams, OperationService} from '../operations/operation.service';
import {Subscription} from 'rxjs';
import {debounceTime, filter} from 'rxjs/operators';
import {MediaCarouselModule} from '../media-carousel/media-carousel.module';
import {OverviewFieldsModule} from '../overview-fields/overview-fields.module';
import {CurrentSearchContext} from '../object-search/current-search.context';
import {
  SelectObjectTemplateButtonComponent
} from '../select-object-template-button/select-object-template-button.component';
import {SearchSelectorService} from '../core/search-selector.service';
import {SelectorContainer} from '../core/definitions/selector-container';
import {ObjectSearchModule} from '../object-search/object-search.module';
import {ObjectPageHeadingComponent} from './ui/object-page-header/object-page-heading/object-page-heading.component';
import {
  ObjectPageOperationsToolbarComponent
} from './ui/object-page-header/object-page-operations-toolbar/object-page-operations-toolbar.component';
import {
  ObjectPageSearchNavigationComponent
} from './ui/object-page-header/object-page-search-navigation/object-page-search-navigation.component';
import {ObjectOverviewFieldsV2Component} from './ui/object-overview-fields-v2/object-overview-fields-v2.component';
import {RefService} from '../core/ref.service';
import {
  FloatingFeatureToggleSwitchComponent
} from '../floating-feature-toggle-switch/floating-feature-toggle-switch.component';
import {ObjectPageNavigationStack} from './object-page-navigation-stack';
import {FeatureFlagsService} from "../core/feature-flags.service";
import {MediaSelectorModule} from "../media-selector/media-selector.module";
import {OperationStepType} from "../core/definitions/operation-step-type.enum";
import {MatButton} from "@angular/material/button";
import {CmsApiService} from "../core/cms-api.service";
import {Selector} from "../core/definitions/reference";
import {SuperObjectModel} from "../core/definitions/super-object-model";
import {OperationExecutorType} from "../core/definitions/operation-executor-type.enum";
import {OperationTarget} from "../core/definitions/operation-target.enum";
import {PrimusRouteService} from "../core/primus-route.service";
import {OperationContainer} from "../core/definitions/operation-container";
import {CurrentObjectService} from "../core/current-object.service";
import {StateParams} from "../core/definitions/state-params";
import {RefParams} from "../core/definitions/ref-params";
import {RefData} from "../core/definitions/ref-data";
import {AConst} from "../core/a-const.enum";

type ActionMenuAndOperationDef = ActionMenuItem & OperationDef
const EDIT_OBJECT_OPERATION_TYPE_ID = 'a4afc969-a015-4648-81b5-eee5a477461c';

function operationToActionMenu(op: OperationDef): ActionMenuAndOperationDef {
  if (op.operation_type === 'operation') {
    return {
      ...op,
      type: 'action',
      label: op.menu_title,
      id: op.menu_title,
      icon: iconToMaterialSymbol(op.menu_icon),
    }
  } else if (op.operation_type === 'group') {
    return {
      ...op,
      type: 'group',
      label: op.menu_title,
      id: op.menu_title,
      icon: iconToMaterialSymbol(op.menu_icon),
      children: op.sub_operations?.map(operationToActionMenu)
    }
  }
}

@Component({
    selector: 'app-object-page-v2',
  imports: [
    MatIcon,
    ObjectPageLayoutComponent,
    RouterOutlet,
    RouterModule,
    ObjectPageNavigationComponent,
    SharedModule,
    MediaCarouselModule,
    OverviewFieldsModule,
    SelectObjectTemplateButtonComponent,
    forwardRef(() => ObjectSearchModule),
    ObjectPageHeadingComponent,
    ObjectPageOperationsToolbarComponent,
    ObjectPageSearchNavigationComponent,
    ObjectOverviewFieldsV2Component,
    FloatingFeatureToggleSwitchComponent,
    MediaSelectorModule,
    MatButton,
  ],
    templateUrl: './object-page-v2.component.html',
    styleUrl: './object-page-v2.component.scss'
})
export class ObjectPageV2Component implements OnDestroy {
  // Injections
  private readonly cms: CmsApiService = inject(CmsApiService);
  private readonly currentObjectService: CurrentObjectService = inject(CurrentObjectService);
  private readonly featureFlagsService: FeatureFlagsService = inject(FeatureFlagsService);
  private readonly mediaService: MediaHelperService = inject(MediaHelperService);
  private readonly objectPageNavigationStack: ObjectPageNavigationStack = inject(ObjectPageNavigationStack);
  private readonly operationDialogService: OperationDialogService = inject(OperationDialogService);
  private readonly operationService: OperationService = inject(OperationService);
  private readonly primusRoute: PrimusRouteService = inject(PrimusRouteService);
  private readonly ref: RefService = inject(RefService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly router: Router = inject(Router);
  private readonly searchSelectorService: SearchSelectorService = inject(SearchSelectorService);
  private readonly translate: TranslateService = inject(TranslateService);
  protected readonly currentObjectContext: CurrentObjectContext = inject(CurrentObjectContext);

  makeCopySelectorContainer: SelectorContainer = null;
  objectTypeName: string = null;
  operationContainer: OperationContainer = null;
  toggleAnnotations = false;
  imageFullScreen = false;
  modelFullscreen = false;

  private artifactHasImages = true;
  selectorContainer = signal<{selectorEnabled: boolean} | null>(null);
  makeCopyOperation = signal(null);
  newReportObject  = signal<any | null>(null);

  disableSelectorContainer() {
    const container = this.selectorContainer();
    if (container) {
      this.searchSelectorService.disableSelector({
        ...container,
        currentMenu: null as any,
        art: null as any,
        searchContainer: null as any,
        selectorCallback: null as any
      });
    }
  }

  @ViewChild(ObjectPageLayoutComponent) layoutComponent: ObjectPageLayoutComponent;

  constructor(
    @Optional() public currentSearchContext?: CurrentSearchContext,
  ) {

    this.subscriptions.push(this.route.paramMap.subscribe((p) => {
      this.headerActions = [];
      this.currentObjectContext.init(p.get('artifactId')).then(() => {
        this.setThumbnail();
        this.maybeResizeImagePanel();
/*
        if (!this.makeCopyOperation()) {
          this.cms.getNewModelInfo({modelName: this.currentObjectContext.art.object_type}).then((data: NewModelInfo) => {
              for (const selector of data.selectors) {
                if (selector.action === 'makeCopy') {
                  this.makeCopyOperation.set(selector);
                }
              }
            },
            () => {
              console.error('Unable to get new model info for ' + this.currentObjectContext.art.object_type);
            }
          );
        }*/
      })
    }),
      this.currentObjectContext.menusChanged$.subscribe(() => {
        this.updateUIElements();
      }),
      this.searchSelectorService.searchSelectorChanged$.subscribe((selectionContainer) => {
        this.selectorContainer.set(selectionContainer);
      }),
      this.router.events.pipe(
        filter((event) => event instanceof NavigationEnd),
      ).subscribe(() => {
        this.isOverviewPage.set(this.route.firstChild.routeConfig.path === 'overview');
      }));

    this.subscriptions.push(this.route.queryParamMap.subscribe((params) => {
      if (params.has('edit')) {
        this.currentObjectContext.isEditing = params.get('edit') === 'true'
        this.updateUIElements()
      }
      if (params.has(AConst.FIELD_TEMPLATE_GROUP_ID)) {
        this.currentObjectContext.onChangeTemplateGroup(params.get(AConst.FIELD_TEMPLATE_GROUP_ID));
      } else {
        this.currentObjectContext.onChangeTemplateGroup(null);
      }
    }))

    this.subscriptions.push(this.operationService.operationStepChange$.pipe(
      debounceTime(200)
    ).subscribe((event) => {

      if (event === 'CANCEL_EDIT') {
        this.currentObjectContext.isEditing = false;
        this.router.navigate(['.'], {
          relativeTo: this.route,
          queryParams: {
            edit: null
          },
          queryParamsHandling: 'merge'
        }).then(() => {
          this.updateUIElements();
        })
      } else {
        const { step } = event

        if (!step) {
          this.router.navigate(['.'], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve'
          })
        }

        if (step?.change_state?.length) {
          const [state] = step.change_state
          const subPage = state.state_params?.listName ?? '.'
          this.refreshObject({});
          // navigate to the child route
          this.router.navigate([subPage], {
            relativeTo: this.route,
            queryParams: state.state_params || {},
            queryParamsHandling: 'merge',
            replaceUrl: true,
          }).then(() => {
            this.updateUIElements();
          })
        }
      }
    }))
  }

  enableSelector(selector: Selector) {
    this.makeCopySelectorContainer = this.searchSelectorService.createSelectorContainer(this.currentObjectContext.art);
    this.searchSelectorService.enableSelector(selector, this.makeCopySelectorContainer, null,
      {
        searchContainerCreated: (/*sc*/) => {},

        selectorCallback: (arts: any[]) => {
          this.selectorCallback(arts);
        }
      }
    );
  }

  private selectorCallback(arts: any[]) {
    if (arts && arts.length > 0) {
      this.searchSelectorService.disableSelector(this.makeCopySelectorContainer);
      this.makeCopySelectorContainer = null;

      console.log('Selected arts', arts);
      console.log('Target art', this.currentObjectContext.art);

      this.createOperationAndSectionsContainer(arts[0]).then(() => {
        /*this.dialog.open(OperationViewDialogComponent, {
          data: {
            operationContainer: this.operationContainer
          }
        })*/

        console.log(this.operationContainer);
      })
      // TODO: This is where new dialog should be triggered
    }
  }

  private async createOperationAndSectionsContainer(object?: SuperObjectModel) {
    const useExisting = !!object;

    const operations = await this.cms.getOperations({
      operation_target: OperationTarget.OBJECT_VIEW,
      object_types: [this.currentObjectContext.art.object_type],
      meta_types: [],
      parent_object_type: this.currentObjectContext.art.object_type,
      parent_meta_type: '',
      operation_contexts: [this.currentObjectContext.art],
      search_result_view_type: '',
      search_view_name: '',
      status_type: ''
    });

    this.operationContainer = await this.operationService.createOperationAndRootSectionsContainer({
      useExistingObject: useExisting,
      objectType: this.currentObjectContext.art.object_type,
      object: object,
      isCopy: useExisting,
      templateGroupId: this.primusRoute.params.template_group_id,
      executorType: OperationExecutorType.ARTIFACT,
      operationStepType: OperationStepType.EDIT_SELECTED_COPY,
      operationTarget: OperationTarget.OBJECT_VIEW,
      usePrimeFields: !useExisting
    } as OperationAndSectionsContainerParams);

    this.currentObjectContext.art = this.operationContainer.rootSectionsContainer.rootObject;
    this.currentObjectService.currentObject = this.currentObjectContext.art;
    this.objectTypeName = this.currentObjectContext.art.object_type;

    this.operationContainer.operations = operations.operations;
  }

  private subscriptions: Subscription[] = [];
  private injector = inject(Injector);

  headerActions: ActionMenuItem[] = [];
  initialActiveChild = this.route.snapshot.firstChild.url.join('/');
  isOverviewPage = signal(this.route.firstChild?.routeConfig.path === 'overview');

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe())
  }

  get artifact() {
    return this.currentObjectContext.art
  }

  get contentInfo() {
    return this.currentObjectContext.contentInfo
  }

  get isEditing() {
    return this.currentObjectContext.isEditing
  }

  get readonly() {
    return this.currentObjectContext.readonly;
  }

  get sectionsContainer() {
    return this.currentObjectContext.sectionsContainer
  }

  get curAnn() {
    return this.currentObjectContext.currAnn
  }

  get objectView() {
    return this.currentObjectContext.sectionsContainer?.objectView
  }

  get loadingNavigationItems() {
    return this.currentObjectContext.loadingOperations
  }

  get loadingArtifact() {
    return this.currentObjectContext.loadingArtifact;
  }

  get loadingMedia() {
    return this.loadingArtifact || this.currentObjectContext.loadingMedia;
  }


  onChangeTemplateGroup(newGroupId: string){
    this.router.navigate(['.'], {
      relativeTo: this.route,
      queryParams: {
        template_group_id: newGroupId
      },
      queryParamsHandling: 'merge'
    })
  }
  thumbnailUrl = signal('')
  placeholderThumbnailIcon = signal('');

  navigationItems: NavMenuItem[] = [];

  operationClick(operation: ActionMenuAndOperationDef) {
    console.log('---- OPERATION CLICK');
    console.log(operation);

    this.currentObjectContext.setCurrentOperation(operation);
    if (operation.operation_type_id === EDIT_OBJECT_OPERATION_TYPE_ID) {
      this.operationService.goNextOperationStep(this.currentObjectContext.operationContainer);

      this.router.navigate(['overview'], {
        relativeTo: this.route,
        queryParams: {
          edit: true
        },
        queryParamsHandling: 'merge'
      })

      this.currentObjectContext.isEditing = true;
      this.refreshObject({})
      this.updateUIElements();
      return
    }

    if (operation.operation_steps[0].operation_step_type === OperationStepType.SELECT_STORE_EXISTING_OBJECTS) {
      this.operationService.openOperationView(this.currentObjectContext.operationContainer, operation);
      return;
    }

    if (operation.operation_type_id === "dbfe80c9-d890-48fe-9fd4-17161d345202") {
      let artifact = this.artifact ||  this.currentSearchContext.findResult(this.currentObjectContext.artifactId);
      if (artifact) {
        this.newReportObject.set({artifact_id: artifact.artifact_id, superobject_type_id: artifact.superobject_type_id, object_type: artifact.object_type});
      } else {
        this.newReportObject.set(null);
      }
    } else {
      this.operationDialogService.openOperationDialog(this.currentObjectContext.operationContainer)
        .then(() => {
          // This check for making sure object lock is not removed for DAMS upload operation
          if (!this.currentObjectContext.operationContainer.currentOperation.$$currentStep.must_lock_current_object) {
            this.currentObjectContext.objectRefresh({})
          }
        });
    }
  }

  refreshObject($event: any) {
    console.log('obj refresh: ', $event);
    this.currentObjectContext.objectRefresh($event)
  }

  openImageFullScreen(activeImage: any) {
    this.toggleAnnotations = activeImage.toggleAnnotations;
    this.imageFullScreen = true;
  }

  openModelFullscreen(_: any) {
    this.toggleAnnotations = false;
    this.modelFullscreen = true;
  }

  closeImageFullScreen() {
    this.imageFullScreen = false;
  }

  closeModelFullscreen() {
    this.modelFullscreen = false;
  }

  private setThumbnail() {
    if (this.artifact) {
      this.placeholderThumbnailIcon.set(objectTypeToMatSymbol(this.artifact.object_type) ?? 'image');
      if (this.artifact.images?.length) {
        const imgId = this.artifact.images[0].image_id
        this.mediaService.getImageUrlsFromImageIds([imgId], ['thumbnail']).then((urls) => {
          this.thumbnailUrl.set(urls[imgId]['thumbnail'])
        });
      } else {
        this.thumbnailUrl.set('')
      }
    }
  }

  private contentMenuToNavMenuItem(contentMenu: ContentMenu): NavMenuItem {
    if (contentMenu.sub_menus?.length) {
      return {
        type: 'section',
        label: toSignal(this.translate.get(contentMenu.caption), { injector: this.injector }),
        disabled: this.currentObjectContext.isEditing,
        icon: iconToMaterialSymbol(contentMenu.icon) ?? getNavigationLinkIcon(contentMenu.content_list),
        children: contentMenu.sub_menus.map(this.contentMenuToNavMenuItem.bind(this)),
        count: contentMenu.count || contentMenu.sub_menus.reduce((acc, subMenu) => acc + (subMenu.count || 0), 0)
      }
    }
    return {
      type: 'link',
      label: toSignal(this.translate.get(contentMenu.caption), { injector: this.injector }),
      disabled: this.currentObjectContext.isEditing,
      link: contentMenu.content_list,
      icon: iconToMaterialSymbol(contentMenu.icon) ?? getNavigationLinkIcon(contentMenu.content_list),
      count: contentMenu.count,
    }
  }

  async onBackClick() {
    const currentIdx = this.currentSearchContext?.findResultIndex(this.artifact.artifact_id);
    if (currentIdx > 0) {
      const prevArtifact = this.currentSearchContext?.searchResults?.[currentIdx - 1];
      const params = new RefParams(prevArtifact, {
        listName: null
      } as StateParams);
      params.searchItemIndex = currentIdx - 1;
      params.rootObjId = prevArtifact.artifact_id;
      const refData = await this.ref.makeRef(params);
      if (refData.stateName.endsWith('artifact')) {
        this.goToArtifact(prevArtifact.artifact_id)
      } else {
        this.goToNotArtifact(refData)
      }
    }
  }

  async onForwardClick() {
    const currentIdx = this.currentSearchContext?.findResultIndex(this.artifact.artifact_id);
    if (currentIdx < this.currentSearchContext?.resultSize - 1) {
      const nextArtifact = this.currentSearchContext?.searchResults?.[currentIdx + 1];
      const params = new RefParams(nextArtifact, {
        listName: null
      } as StateParams);
      params.searchItemIndex = currentIdx + 1;
      params.rootObjId = nextArtifact.artifact_id;
      const refData = await this.ref.makeRef(params);
      if (refData.stateName.endsWith('artifact')) {
        this.goToArtifact(nextArtifact.artifact_id)
      } else {
        this.goToNotArtifact(refData)
      }
    }
  }

  onCloseClick() {
    const prevRoute = this.objectPageNavigationStack.pop();
    if (this.currentSearchContext) {
      this.router.navigate(['/search'], {
        queryParams: {
          artifactId: null,
        },
        queryParamsHandling: 'merge'
      })
    } else if (prevRoute) {
      this.router.navigateByUrl(prevRoute);
    } else {
      this.router.navigate(['/'])
    }
  }

  private goToArtifact(artifactId: string) {
    if (!location.pathname.startsWith('/search/artifact')) {
      this.router.navigate(['/artifact', artifactId], {
        queryParams: {
          artifactId
        },
        queryParamsHandling: 'merge'
      }).then();
    }
    else {
      this.router.navigate(['..', artifactId], {
        relativeTo: this.route,
        queryParams: {
          artifactId
        },
        queryParamsHandling: 'merge'
      })
    }
  }

  private goToNotArtifact(refData: RefData) {
    this.router.navigate(refData.routerLink, {
      queryParams: refData.queryParams,
      queryParamsHandling: 'merge'
    })
  }

  private updateUIElements() {
    this.headerActions = this.currentObjectContext.operationContainer?.operations?.map(operationToActionMenu) ?? []
    this.navigationItems = [
      ...this.currentObjectContext.menus.map(this.contentMenuToNavMenuItem.bind(this)),
      {
        type: 'group',
        label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__CHANGES'), { injector: this.injector }),
        disabled: this.currentObjectContext.isEditing,
        link: 'changelog',
        icon: 'history',
        children: [
          {
            type: 'link',
            label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__FOLDERS'), { injector: this.injector }),
            disabled: this.currentObjectContext.isEditing,
            link: 'changelog/folders',
          },
          {
            type: 'link',
            label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__CHANGES'), { injector: this.injector }),
            disabled: this.currentObjectContext.isEditing,
            link: 'changelog/changes',
          },
          {
            type: 'link',
            label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__IDENTIFICATION_NUMBER_LOG'), { injector: this.injector }),
            disabled: this.currentObjectContext.isEditing,
            link: 'changelog/id-numbers',
          },
          {
            type: 'link',
            label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__COLLECTION_LOG'), { injector: this.injector }),
            disabled: this.currentObjectContext.isEditing,
            link: 'changelog/collection-log',
          },
          {
            type: 'link',
            label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__COLLECTION_STATUS_LOG'), { injector: this.injector }),
            disabled: this.currentObjectContext.isEditing,
            link: 'changelog/collection-status-log',
          },
          {
            type: 'link',
            label: toSignal(this.translate.get('TRANS__OBJECT_INFORMATION_TABS__QUANTITY_LOG'), { injector: this.injector }),
            disabled: this.currentObjectContext.isEditing,
            link: 'changelog/quantity-log',
          },
        ]
      }
    ] as NavMenuItem[]
  }

  navigateToOldDesign() {
    // use a small timeout to allow the slider animation to finish
    setTimeout(() => {
      if (this.currentSearchContext) {
        this.router.navigate(['/search/artifact'], {
          queryParamsHandling: 'merge'
        })
      } else {
        this.router.navigate(['/artifact'], {
          queryParamsHandling: 'merge'
        })
      }
    }, 200)
  }

  private maybeResizeImagePanel() {
    const previousHadImages = this.artifactHasImages;
    this.artifactHasImages = this.artifact?.images?.length > 0;
    if (previousHadImages !== this.artifactHasImages) {
      this.layoutComponent.setRightDrawerWidth(this.artifactHasImages ? 'min(100vw, 500px)' : '150px');
    }
  }

  readonly useNewLayout = this.featureFlagsService.getFeatureFlags()?.experimental?.useNewMainMenu;
}

/**
 * Menu icons are usually defined in the API. To prevent breaking changes there for the old object page,
 * we need to map the old icon names to the new material icons.
 * @param name name of the navigation item/content list
 */
function getNavigationLinkIcon(name: string): string {

  switch (name) {
    case 'overview':
      return 'list_alt';
    case 'relations':
      return 'device_hub';
    case 'uploads':
      return 'attach_file';
    case 'conservation':
      return 'format_paint';
    case 'information':
      return 'info';
    case 'activities':
      return 'task';
    case 'transference':
      return 'my_location';
    case 'spectrum_procedures':
    case 'spectrum_procedure':
      return 'timeline';
  }

  return 'category';

}
